import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { Menu, X, ChevronDown, Plus } from "lucide-react";
import { NAV2, BOOKING_URL } from "../mock/mock";
import Logo from "../components/Logo";

function DesktopItem({ item }) {
  const location = useLocation();
  const active = location.pathname === item.to || (item.children || []).some((c) => location.pathname === c.to.split("#")[0]);
  const [open, setOpen] = useState(false);

  if (!item.children) {
    return (
      <Link to={item.to} className={`font-grotesk uppercase text-[13px] tracking-wide transition-colors ${active ? "text-sc-lime" : "text-white/75 hover:text-sc-lime"}`}>
        {item.label}
      </Link>
    );
  }
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <Link to={item.to} className={`font-grotesk uppercase text-[13px] tracking-wide inline-flex items-center gap-1 transition-colors ${active ? "text-sc-lime" : "text-white/75 hover:text-sc-lime"}`}>
        {item.label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </Link>
      <div className={`absolute left-1/2 -translate-x-1/2 top-full pt-4 w-64 transition-all duration-200 ${open ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"}`}>
        <div className="bg-[#0b241a] border border-sc-lime/25 rounded-xl shadow-2xl overflow-hidden">
          {item.children.map((c) => (
            <Link key={c.label} to={c.to} className="block px-5 py-3 font-grotesk text-sm text-white/80 hover:bg-sc-lime hover:text-[#06170f] transition-colors border-b border-white/5 last:border-0">
              {c.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Draft2Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const location = useLocation();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -80 }} animate={{ y: 0 }} transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "bg-[#06170f]/90 backdrop-blur-xl border-b border-white/10" : "bg-gradient-to-b from-black/50 to-transparent"}`}
      >
        <nav className="max-w-[1400px] mx-auto px-5 lg:px-8 h-[74px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <Logo size={44} />
            <span className="hidden xl:block font-grotesk font-bold uppercase tracking-[0.18em] text-sm text-white">The Supreme Courts</span>
          </Link>

          <div className="hidden lg:flex items-center gap-7">
            {NAV2.map((item) => <DesktopItem key={item.label} item={item} />)}
          </div>

          <div className="flex items-center gap-3">
            <a href={BOOKING_URL} className="hidden sm:inline-flex d2-btn px-6 py-2.5 text-xs">Book Now!</a>
            <button className="lg:hidden text-white" onClick={() => setMobileOpen(true)} aria-label="menu"><Menu className="w-7 h-7" /></button>
          </div>
        </nav>
        <motion.div className="h-[3px] bg-sc-lime origin-left" style={{ scaleX }} />
      </motion.header>

      {/* Mobile menu */}
      <motion.div
        initial={false}
        animate={{ opacity: mobileOpen ? 1 : 0, pointerEvents: mobileOpen ? "auto" : "none" }}
        className="fixed inset-0 z-[60] bg-[#06170f] flex flex-col overflow-y-auto"
      >
        <div className="h-[74px] px-5 flex items-center justify-between border-b border-white/10 shrink-0">
          <Logo size={40} />
          <button className="text-white" onClick={() => setMobileOpen(false)} aria-label="close"><X className="w-7 h-7" /></button>
        </div>
        <div className="flex flex-col py-4 px-6">
          {NAV2.map((item) => (
            <div key={item.label} className="border-b border-white/10">
              <div className="flex items-center justify-between">
                <Link to={item.to} className="font-display uppercase text-3xl text-white/90 hover:text-sc-lime py-3">{item.label}</Link>
                {item.children && (
                  <button onClick={() => setExpanded(expanded === item.label ? null : item.label)} className="text-sc-lime p-2">
                    <Plus className={`w-6 h-6 transition-transform ${expanded === item.label ? "rotate-45" : ""}`} />
                  </button>
                )}
              </div>
              {item.children && expanded === item.label && (
                <div className="pb-3 pl-2">
                  {item.children.map((c) => (
                    <Link key={c.label} to={c.to} className="block py-2 font-grotesk text-white/60 hover:text-sc-lime">{c.label}</Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <a href={BOOKING_URL} className="d2-btn px-8 py-4 mt-6 w-fit">Book Now!</a>
        </div>
      </motion.div>
    </>
  );
}
